import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import FileUpload from "../helper/FileUpload";
import { fetchData } from "../helper/api.interceptor";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";

const AddColorVariant = () => {
    const navigate = useNavigate();
    const { productId } = useParams();

    const [variants, setVariants] = useState([
        {
            color_name: "",
            is_default: false,
            productImages: [],
            mainImages: [],
            colorVariantListingImage: [],
            // newArrivalListImages: [],
            placeOrderImages: [],
        },
    ]);

    const [loading, setLoading] = useState(false);

    /* ---------------- HELPERS ---------------- */

    const appendFilesToFormData = (formData, fieldName, images) => {
        if (!images || !images.length) return;
        images.forEach((img) => {
            if (img?.file) formData.append(fieldName, img.file);
        });
    };

    /* ---------------- HANDLERS ---------------- */

    const handleChange = (index, field, value) => {
        const updated = [...variants];
        updated[index][field] = value;
        setVariants(updated);
    };

    const addVariant = () => {
        setVariants([
            ...variants,
            {
                color_name: "",
                is_default: false,
                productImages: [],
                mainImages: [],
                colorVariantListingImage: [],
                // newArrivalListImages: [],
                placeOrderImages: [],
            },
        ]);
    };

    const removeVariant = (index) => {
        setVariants(variants.filter((_, i) => i !== index));
    };

    /* ---------------- SAVE ALL ---------------- */

    /* ---------------- SKIP (no API call) ---------------- */
    const handleSkip = () => {
        navigate(`/product/add-specification/${productId}`);
    };

    const handleSaveAll = async (goToNext = true) => {
        if (!variants.length) {
            Swal.fire("Warning", "Please add at least one color variant", "warning");
            return;
        }

        for (const variant of variants) {
            if (!variant.color_name) {
                Swal.fire("Missing Fields", "Color name is required", "warning");
                return;
            }
        }

        setLoading(true);

        try {
            for (const variant of variants) {
                const formData = new FormData();

                formData.append("color_name", variant.color_name);
                formData.append("is_default", variant.is_default);

                appendFilesToFormData(formData, "productImages", variant.productImages);
                appendFilesToFormData(formData, "mainImages", variant.mainImages);
                appendFilesToFormData(formData, "color_variant_listing_image", variant.colorVariantListingImage);
                // appendFilesToFormData(formData, "new_arrival_list", variant.newArrivalListImages);
                appendFilesToFormData(formData, "place_order_images", variant.placeOrderImages);

                const result = await fetchData.postWithFormData(
                    `product/add-color-variant/${productId}`,
                    formData
                );

                if (result?.status_code !== 200) {
                    Swal.fire(
                        "Error",
                        result?.message || "Failed to add color variant",
                        "error"
                    );
                    return; // stop if one fails
                }
            }

            Swal.fire({
                icon: "success",
                title: "Saved!",
                text: "All color variants added successfully",
            }).then(() => {
                if (goToNext) {
                    navigate(`/product/add-specification/${productId}`);
                }
            });
        } catch (error) {
            console.error(error);
            Swal.fire("Oops...", "Something went wrong", "error");
        } finally {
            setLoading(false);
        }
    };

    /* ---------------- UI ---------------- */

    return (
        <div>
            <h6 className="fw-semibold mb-3">Add Color Variants</h6>

            {variants.map((variant, index) => (
                <div className="card mb-3 p-3" key={index}>
                    <div className="d-flex justify-content-end">
                        {variants.length > 1 && (
                            <button
                                className="btn btn-danger btn-sm"
                                style={{ backgroundColor: "#6b757d", borderColor: "#6b757d" }}
                                onClick={() => removeVariant(index)}
                            >
                                Remove
                            </button>
                        )}
                    </div>

                    {/* Color Name */}
                    <div className="mb-3">
                        <label className="form-label fw-semibold">Color Name</label>
                        <input
                            type="text"
                            className="form-control"
                            value={variant.color_name}
                            onChange={(e) =>
                                handleChange(index, "color_name", e.target.value)
                            }
                        />
                    </div>

                    {/* Default */}
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={variant.is_default}
                                onChange={(e) =>
                                    handleChange(index, "is_default", e.target.checked)
                                }
                            />
                        }
                        label="Set as Default Color"
                    />

                    <FileUpload
                        label="Product Images"
                        multiple
                        images={variant.productImages}
                        onImagesChange={(files) =>
                            handleChange(index, "productImages", files)
                        }
                    />

                    <FileUpload
                        label="Main Images"
                        multiple
                        images={variant.mainImages}
                        onImagesChange={(files) =>
                            handleChange(index, "mainImages", files)
                        }
                    />

                    <FileUpload
                        label="Color Variant Listing Image"
                        multiple={false}
                        maxFiles={1}
                        images={variant.colorVariantListingImage}
                        onImagesChange={(files) =>
                            handleChange(index, "colorVariantListingImage", files)
                        }
                    />

                    {/* <FileUpload
                        label="New Arrival List Images"
                        multiple
                        images={variant.newArrivalListImages}
                        onImagesChange={(files) =>
                            handleChange(index, "newArrivalListImages", files)
                        }
                    /> */}

                    <FileUpload
                        label="Place Order Images"
                        multiple={false}
                        images={variant.placeOrderImages}
                        onImagesChange={(files) =>
                            handleChange(index, "placeOrderImages", files)
                        }
                    />

                </div>
            ))}

            <div className="d-flex gap-2 flex-wrap">
                <button className="btn btn-primary" onClick={addVariant}>
                    Add Other
                </button>

                <button
                    className="btn btn-success"
                    onClick={() => handleSaveAll(true)}
                    disabled={loading}
                >
                    {loading ? "Saving..." : "Save"}
                </button>

                <button
                    className="btn btn-secondary"
                    onClick={handleSkip}
                    disabled={loading}
                >
                    Skip
                </button>
            </div>
        </div>
    );
};

export default AddColorVariant;
